import type { UserComponentPortDesc } from "@/types/userComponent";
import type { ModuleProperty } from "../typeDefinations";

type UserNumericValue = number | string;

type CompiledExpression = (
  componentProperties: Record<string, number>,
  variables: Record<string, number>
) => unknown;

const MAX_EXPRESSION_CACHE_SIZE = 1000;
const expressionCache = new Map<string, CompiledExpression>();

type DynamicPortDesc = UserComponentPortDesc & {
  position?: [UserNumericValue, UserNumericValue];
  x?: UserNumericValue;
  y?: UserNumericValue;
  xExpr?: string;
  yExpr?: string;
};

export type RenderContext = {
  center: [number, number];
  properties: ModuleProperty;
  componentProperties: ModuleProperty;
  scaleX: number;
  scaleY: number;
  minScale: number;
};

const toFiniteNumber = (value: any, fallback = 0): number => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const compileExpression = (expression: string): CompiledExpression => {
  const cached = expressionCache.get(expression);
  if (cached) return cached;

  const normalized = expression.replace(
    /\$([A-Za-z_][\w]*)/g,
    (_, key: string) => "componentProperties[" + JSON.stringify(key) + "]"
  );

  const compiled = new Function(
    "componentProperties",
    "vars",
    `with (vars) { return (${normalized}); }`
  ) as CompiledExpression;

  if (expressionCache.size >= MAX_EXPRESSION_CACHE_SIZE) {
    const oldestKey = expressionCache.keys().next().value;
    if (oldestKey !== undefined) expressionCache.delete(oldestKey);
  }
  expressionCache.set(expression, compiled);
  return compiled;
};

export const propertyValue = (
  properties: ModuleProperty,
  key: string,
  fallback = 0
): number => toFiniteNumber((properties as any)?.[key]?.value, fallback);

export const evaluateUserExpression = (
  expression: string,
  properties: ModuleProperty,
  fallback = 0,
  vars: Record<string, number> = {}
): number => {
  try {
    const p: Record<string, number> = {};
    for (const key in properties) p[key] = propertyValue(properties, key, 0);
    return toFiniteNumber(
      compileExpression(expression)(p, vars),
      fallback
    );
  } catch (e) {
    console.warn("Expression evaluation error:", expression, e);
    return fallback;
  }
};

export const readUserNumber = (
  value: any,
  properties: ModuleProperty,
  fallback = 0,
  vars: Record<string, number> = {}
): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string") {
    const direct = Number(value);
    return Number.isFinite(direct)
      ? direct
      : evaluateUserExpression(value, properties, fallback, vars);
  }
  return fallback;
};

export const resolvePortPosition = (
  port: DynamicPortDesc,
  ctx: RenderContext
): [number, number] => {
  const cx = ctx.center[0];
  const cy = ctx.center[1];

  if (port.xExpr || port.yExpr) {
    return [
      cx + (port.xExpr ? evaluateUserExpression(port.xExpr, ctx.properties) : 0),
      cy + (port.yExpr ? evaluateUserExpression(port.yExpr, ctx.properties) : 0)
    ];
  }

  if (port.position) {
    return [
      cx + readUserNumber(port.position[0], ctx.properties),
      cy + readUserNumber(port.position[1], ctx.properties)
    ];
  }

  return [
    cx + readUserNumber(port.x ?? 0, ctx.properties),
    cy + readUserNumber(port.y ?? 0, ctx.properties)
  ];
};
